import React from "react";
import { Link } from "react-router-dom";
import { AnalyticsOverview, DenimUploadBatchStatus, FactoredInvoiceStatus, MetricRate } from "../../../../app/data/invoice-factoring/models";
import { BATCH_STATUS_LABELS, INVOICE_STATUS_LABELS, ISSUE_TYPE_LABELS, formatCents } from "../constants";
import { formatDuration, formatPayor, formatPercent, formatRate, ratePercent } from "../utils/metrics";

const INVOICE_STATUSES = Object.keys(INVOICE_STATUS_LABELS) as FactoredInvoiceStatus[];

/** positive: higher is better; negative: lower is better; neutral: informational. */
type RateTone = "positive" | "negative" | "neutral";

interface RateRow {
  label: string;
  description: string;
  rate: MetricRate;
  tone: RateTone;
}

const RateList: React.FC<{ rows: RateRow[] }> = ({ rows }) => (
  <ul className="xgs-invoice-factoring__rates">
    {rows.map((row) => (
      <li key={row.label} className="xgs-invoice-factoring__rate">
        <div className="xgs-invoice-factoring__rate-text">
          <div className="xgs-invoice-factoring__rate-label">{row.label}</div>
          <div className="xgs-invoice-factoring__muted">{row.description}</div>
        </div>
        <div className="xgs-invoice-factoring__rate-value">
          <strong>{formatPercent(row.rate)}</strong>
          <span>
            {row.rate.numerator} of {row.rate.denominator}
          </span>
        </div>
        <div className="xgs-invoice-factoring__rate-bar">
          <div
            className={`xgs-invoice-factoring__rate-fill xgs-invoice-factoring__rate-fill--${row.tone}`}
            style={{ width: `${ratePercent(row.rate)}%` }}
          />
        </div>
      </li>
    ))}
  </ul>
);

export const PayorsTable: React.FC<{ overview: AnalyticsOverview }> = ({ overview }) => {
  const { top } = overview.payors;
  if (!top.length) return <div className="xgs-invoice-factoring__muted">No invoices tracked in the selected range.</div>;
  return (
    <div className="xgs-invoice-factoring__table-scroll">
      <table className="xgs-invoice-factoring__doc-table xgs-invoice-factoring__doc-table--wide">
        <thead>
          <tr>
            <th>Payor</th>
            <th>Invoices</th>
            <th>Value</th>
            <th>Submission complete</th>
            <th>Needs attention</th>
            <th>Document gaps</th>
            <th>Median time</th>
          </tr>
        </thead>
        <tbody>
          {top.map((payor) => (
            <tr key={payor.payorNumber ?? "unknown"}>
              <td>{formatPayor(payor.payorName, payor.payorNumber)}</td>
              <td className="xgs-invoice-factoring__metric-value">{payor.trackedInvoices}</td>
              <td className="xgs-invoice-factoring__metric-value">{formatCents(payor.trackedValueCents)}</td>
              <td className="xgs-invoice-factoring__metric-value">{formatRate(payor.submissionComplete)}</td>
              <td className="xgs-invoice-factoring__metric-value">
                <span className={payor.needsAttention ? "xgs-invoice-factoring__text--error" : undefined}>
                  {payor.needsAttention}
                </span>
              </td>
              <td className="xgs-invoice-factoring__metric-value">{payor.documentGaps}</td>
              <td className="xgs-invoice-factoring__metric-value">{formatDuration(payor.p50Seconds)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const PipelineTable: React.FC<{
  overview: AnalyticsOverview;
  linkForStatus: (status: FactoredInvoiceStatus) => string;
}> = ({ overview, linkForStatus }) => {
  const { pipeline } = overview;
  return (
    <table className="xgs-invoice-factoring__doc-table xgs-invoice-factoring__doc-table--wide">
      <thead>
        <tr>
          <th>Status</th>
          <th>Invoices</th>
          <th>Value</th>
          <th>Share</th>
        </tr>
      </thead>
      <tbody>
        {INVOICE_STATUSES.map((status) => {
          const row = pipeline.byStatus.find((candidate) => candidate.status === status);
          return (
            <tr key={status}>
              <td>
                <Link
                  className="xgs-table-link-cell"
                  to={linkForStatus(status)}
                  title="Open these invoices"
                >
                  {INVOICE_STATUS_LABELS[status]}
                </Link>
              </td>
              <td className="xgs-invoice-factoring__metric-value">{row?.count ?? 0}</td>
              <td className="xgs-invoice-factoring__metric-value">{formatCents(row?.valueCents ?? 0)}</td>
              <td className="xgs-invoice-factoring__metric-value">
                {formatRate({ numerator: row?.count ?? 0, denominator: pipeline.trackedInvoices })}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export const IssuesTable: React.FC<{ overview: AnalyticsOverview }> = ({ overview }) => (
  <table className="xgs-invoice-factoring__doc-table xgs-invoice-factoring__doc-table--wide">
    <thead>
      <tr>
        <th>Reason</th>
        <th>Invoices</th>
        <th>Value</th>
      </tr>
    </thead>
    <tbody>
      {overview.issues.byType.map((issue) => (
        <tr key={issue.type}>
          <td>{ISSUE_TYPE_LABELS[issue.type]}</td>
          <td className="xgs-invoice-factoring__metric-value">{issue.count}</td>
          <td className="xgs-invoice-factoring__metric-value">{formatCents(issue.valueCents)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const BatchMonitorTable: React.FC<{ overview: AnalyticsOverview }> = ({ overview }) => {
  const { batches, batchesByStatus, rows } = overview.batchMonitor;
  const rowCounts = [
    { label: "Rows uploaded", value: rows.total },
    { label: "Valid rows", value: rows.valid },
    { label: "Invalid rows", value: rows.invalid },
    { label: "Duplicates in file", value: rows.duplicateInFile },
    { label: "Waiting", value: rows.waiting },
    { label: "Sending", value: rows.processing },
    { label: "Sent", value: rows.completed },
    { label: "Already sent", value: rows.skipped },
    { label: "Failed", value: rows.failed },
  ];
  const statuses = (Object.keys(BATCH_STATUS_LABELS) as DenimUploadBatchStatus[]).filter(
    (status) => batchesByStatus[status]
  );

  return (
    <>
      <div className="xgs-invoice-factoring__stats">
        <div>
          <span>Batches</span>
          <strong>{batches}</strong>
        </div>
        {statuses.map((status) => (
          <div key={status}>
            <span>{BATCH_STATUS_LABELS[status]}</span>
            <strong>{batchesByStatus[status]}</strong>
          </div>
        ))}
      </div>
      <table className="xgs-invoice-factoring__doc-table xgs-invoice-factoring__doc-table--wide">
        <thead>
          <tr>
            <th>Rows</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {rowCounts.map((row) => (
            <tr key={row.label}>
              <td>{row.label}</td>
              <td className="xgs-invoice-factoring__metric-value">{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export const RetryHealthTable: React.FC<{ overview: AnalyticsOverview }> = ({ overview }) => {
  const { retryHealth } = overview;
  return (
    <RateList
      rows={[
        {
          label: "First-attempt success",
          description: "Invoices sent completely without a failed or skipped attempt, of invoices with an attempt",
          rate: retryHealth.firstAttemptSuccess,
          tone: "positive",
        },
        {
          label: "Invoices that needed a retry",
          description: "A step ran more than once, of invoices with an attempt",
          rate: retryHealth.retriedInvoices,
          tone: "negative",
        },
        {
          label: "Retries that recovered",
          description: "Retried invoices that are now sent completely, of retried invoices",
          rate: retryHealth.retriedRecovered,
          tone: "positive",
        },
        {
          label: "Unresolved failures",
          description: "Invoices not sent completely whose latest attempt failed or was skipped, of tracked invoices",
          rate: retryHealth.unresolvedFailures,
          tone: "negative",
        },
        {
          label: "Failed attempts",
          description: "Failed or duplicate-on-Denim attempts, of attempts started in the range",
          rate: retryHealth.failedAttempts,
          tone: "negative",
        },
      ]}
    />
  );
};

export const DuplicatePreventionTable: React.FC<{ overview: AnalyticsOverview }> = ({ overview }) => {
  const { duplicatePrevention } = overview;
  return (
    <RateList
      rows={[
        {
          label: "Duplicate rows in files",
          description: "Rows repeating a probill already in the same file, of rows uploaded",
          rate: duplicatePrevention.duplicateRowsInFiles,
          tone: "neutral",
        },
        {
          label: "Previously sent probills skipped",
          description: "Invoices whose Denim job already existed, so job creation was skipped, of invoices with a job step",
          rate: duplicatePrevention.previouslySentSkipped,
          tone: "neutral",
        },
        {
          label: "Invoices in more than one batch",
          description: "Of tracked invoices",
          rate: duplicatePrevention.multipleBatches,
          tone: "neutral",
        },
      ]}
    />
  );
};
